/**
 * DEFCAT-FU-04/FU-05 — resolve a catalogue location's L0 **component group**.
 *
 * After the table split + reshape, `componentGroupId` sits on the entry that
 * attaches to L0: the leaf itself for flat catalogues (AERO, Vestas), or the L1
 * group for nested ones (Enercon `location_group`s, Nordex Outside/Inside).
 * Descendants inherit it via the `parentEntryId` chain. These pure helpers let
 * the drawer scope the location picker to the analyst-selected component group
 * (D4a: L0 is auto-preselected from the data point but overridable).
 *
 * Pure (no React) so the tree-walk is unit-tested in isolation.
 */
import type { DefectCatalogueEntryV2 } from "@aeriosiq/types";

/** Walk parentEntryId upward until a `componentGroupId` is found. */
export function effectiveComponentGroupId(
  entry: DefectCatalogueEntryV2,
  byId: ReadonlyMap<string, DefectCatalogueEntryV2>,
): string | null {
  let cur: DefectCatalogueEntryV2 | undefined = entry;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.entryId)) {
    if (cur.componentGroupId) return cur.componentGroupId;
    seen.add(cur.entryId);
    const pid: string | null = cur.parentEntryId ?? null;
    cur = pid ? byId.get(pid) : undefined;
  }
  return null;
}

/**
 * The set of LOCATION entry ids whose effective component group is `groupId`.
 * Used to filter `locationLeafOptions` to the selected L0. A null `groupId`
 * (no component group resolvable / not selected) returns an empty set, and the
 * caller treats "no scope" as "show all".
 */
export function locationEntryIdsForComponentGroup(
  entries: readonly DefectCatalogueEntryV2[],
  groupId: string | null,
): Set<string> {
  const out = new Set<string>();
  if (!groupId) return out;
  const locations = entries.filter((e) => e.axis === "location");
  const byId = new Map<string, DefectCatalogueEntryV2>(
    locations.map((e) => [e.entryId, e]),
  );
  for (const e of locations) {
    if (effectiveComponentGroupId(e, byId) === groupId) out.add(e.entryId);
  }
  return out;
}

/** The distinct component-group ids that this catalogue's location axis uses
 *  (so the override selector can offer only groups the catalogue can annotate). */
export function componentGroupIdsInUse(
  entries: readonly DefectCatalogueEntryV2[],
): Set<string> {
  const out = new Set<string>();
  const locations = entries.filter((e) => e.axis === "location");
  const byId = new Map<string, DefectCatalogueEntryV2>(
    locations.map((e) => [e.entryId, e]),
  );
  for (const e of locations) {
    const g = effectiveComponentGroupId(e, byId);
    if (g) out.add(g);
  }
  return out;
}
